import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Globe3D from '../components/Globe';
import { 
  ArrowRight, Server, Activity, Plane, ChevronDown,
  Sparkles, Shield, Zap, Users, Globe, TrendingUp,
  Target, Code2, Heart, Quote
} from 'lucide-react';

function HomePage() {
  const navigate = useNavigate();

  const whyChooseUs = [
    {
      icon: <Users className="w-12 h-12" />,
      title: "20+ Years Experienced Team",
      description: "Deep domain expertise for complex challenges"
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: "Innovation-Driven",
      description: "AI, cloud-native, and scalable solutions"
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: "Proven Results",
      description: "100+ satisfied clients worldwide"
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Industry Focused",
      description: "Healthcare, Travel, Legacy Modernization"
    }
  ];

  const products = [
    {
      icon: <Server className="w-12 h-12" />,
      title: "Legacy Modernization",
      description: "Upgrade your core systems with zero disruption. Modernize COBOL/mainframe to cloud.",
      link: "/products/legacy-modernization",
      cta: "Learn More",
      image: "https://customer-assets.emergentagent.com/job_future-code-1/artifacts/enw5fqza_Gemini_Generated_Image_puzpuepuzpuepuzp.png"
    },
    {
      icon: <Activity className="w-12 h-12" />,
      title: "Healthcare Solutions",
      description: "AI-powered EMR/HIS, smart kiosks for next-gen care & efficient hospital management.",
      link: "/products/healthcare",
      cta: "Explore Product",
      image: "https://images.unsplash.com/photo-1606206873764-fd15e242df52"
    },
    {
      icon: <Plane className="w-12 h-12" />,
      title: "TripTrust CRM",
      description: "End-to-end travel CRM: automate leads, bookings, and feedback.",
      link: "/products/triptrust",
      cta: "View Demo",
      image: "https://images.unsplash.com/photo-1586866991393-43a5cfd717bd"
    }
  ];

  const testimonials = [
    {
      quote: "PrimeFields helped us digitize legacy systems without downtime – true experts!",
      author: "Enterprise Client",
      industry: "Banking & Finance"
    },
    {
      quote: "Our hospital workflow is now seamless and data-driven.",
      author: "Healthcare Partner",
      industry: "Healthcare"
    },
    {
      quote: "The travel CRM transformed how we manage our customer relationships.",
      author: "Travel Agency",
      industry: "Travel & Tourism"
    }
  ];

  const stats = [
    { number: "20+", label: "Years Experience" },
    { number: "100+", label: "Clients Served" },
    { number: "3", label: "Core Products" },
    { number: "Global", label: "Reach" }
  ];

  const scrollToProducts = () => {
    document.getElementById('products').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero-section" style={{ background: 'linear-gradient(180deg, #0a0e1a 0%, #1a2332 100%)' }}>
        <div className="hero-bg" style={{ display: 'none' }}></div>
        <div className="hero-globe-bg">
          <Globe3D className="hero-globe" />
        </div>
        <div className="hero-layout">
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkles className="w-4 h-4" />
              <span>Technology Transformation Experts</span>
            </div>
            <h1 className="hero-title">
              Powering Digital<br />Transformation Across Industries
            </h1>
            <p className="hero-subtitle">
              From legacy systems to modern healthcare and travel solutions—we build technology that transforms businesses
            </p>
            <div className="hero-cta">
              <Button 
                size="lg" 
                className="cta-primary"
                onClick={scrollToProducts}
              >
                Explore Our Products <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="cta-secondary"
                onClick={() => navigate('/contact')}
              >
                Get In Touch
              </Button>
            </div>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={scrollToProducts}>
          <ChevronDown className="w-6 h-6 animate-bounce" />
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Why Choose Us</Badge>
            <h2 className="section-title">Your Trusted Technology Partner</h2>
            <p className="section-description">
              Combining expertise, innovation, and proven results to deliver exceptional solutions
            </p>
          </div>

          <div className="why-choose-grid">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="why-choose-card">
                <CardHeader>
                  <div className="why-choose-icon">{item.icon}</div>
                  <CardTitle className="why-choose-title">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="why-choose-description">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Products Section */}
      <section id="products" className="section section-alt">
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Our Products</Badge>
            <h2 className="section-title">Core Product Spotlights</h2>
            <p className="section-description">
              Industry-specific platforms built with expertise, innovation, and a commitment to excellence
            </p>
          </div>

          <div className="products-showcase">
            {products.map((product, index) => (
              <Card key={index} className="product-showcase-card" onClick={() => navigate(product.link)}>
                <div className="product-image" style={{ backgroundImage: `url(${product.image})` }}>
                  <div className="product-image-overlay">
                    <div className="product-icon">{product.icon}</div>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="product-showcase-title">{product.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="product-showcase-description">
                    {product.description}
                  </CardDescription>
                  <Button variant="ghost" className="product-learn-more">
                    {product.cta} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Transformation Stories Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Success Stories</Badge>
            <h2 className="section-title">Transformation Stories</h2>
            <p className="section-description">
              Hear from our clients who have transformed their businesses with our solutions
            </p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <CardHeader>
                  <Quote className="w-10 h-10" style={{ color: 'var(--accent)', opacity: 0.3 }} />
                </CardHeader>
                <CardContent>
                  <p className="testimonial-quote">"{testimonial.quote}"</p>
                  <div className="testimonial-author">
                    <p className="author-name">{testimonial.author}</p>
                    <p className="author-industry">{testimonial.industry}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Business?</h2>
            <p className="cta-description">
              Let's discuss how our solutions can help you achieve your goals
            </p>
            <Button 
              size="lg" 
              className="cta-primary"
              onClick={() => navigate('/contact')}
            >
              Contact Us Today <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
